// mirror of auware/.../frontend/src/components/ui/Alert.tsx (see frontend/CLAUDE.md mirror policy).
import { forwardRef, type HTMLAttributes } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Visual style of the alert. */
type AlertVariant = "info" | "success" | "warning" | "error";

export interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  /** Visual variant. */
  variant: AlertVariant;
  /** Optional bold title displayed above the body text. */
  title?: string;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const baseClasses = "rounded-md p-4";

const variantClasses: Record<AlertVariant, string> = {
  info: "bg-blue-50 text-blue-700",
  success: "bg-emerald-50 text-emerald-700",
  warning: "bg-amber-50 text-amber-700",
  error: "bg-red-50 text-red-700",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Info / success / warning / error banner.
 *
 * ```tsx
 * <Alert variant="error" title="Something went wrong">
 *   Please try again later.
 * </Alert>
 * ```
 */
const Alert = forwardRef<HTMLDivElement, AlertProps>(
  ({ className = "", variant, title, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        role="alert"
        className={`${baseClasses} ${variantClasses[variant]} ${className}`}
        {...props}
      >
        {title && <p className="text-sm font-medium">{title}</p>}
        {children ? <div className="text-sm">{children}</div> : null}
      </div>
    );
  },
);

Alert.displayName = "Alert";

export default Alert;
